import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tap } from 'rxjs';
import { PhoneFormatModel, ProfileWebModel } from '@data-access';
import { SessionLifecycle } from '../services/session-lifecycle.service';

interface SessionState {
  tabId: string | undefined;
  profile: ProfileWebModel | undefined;
  /** `null` until the server has answered: an empty list is a valid answer, not a cache miss. */
  countriesPhoneMasks: PhoneFormatModel[] | null;
}

const initialState: SessionState = {
  tabId: undefined,
  profile: undefined,
  countriesPhoneMasks: null,
};

/** Internal to core-session: other libs read the session through `UserService` / `CoreService`. */
export const SessionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ profile }) => ({
    currentCarrierId: computed(() => profile()?.access?.carriers?.[0]?.carrierId ?? ''),
    currentCustomerId: computed(() => profile()?.access?.customers?.[0]?.customerId ?? ''),
  })),
  withMethods((store) => ({
    setTabId(tabId: string): void {
      patchState(store, { tabId });
    },
    setProfile(profile: ProfileWebModel): void {
      patchState(store, { profile });
    },
    /** A profile edit response carries no access rights, so the stored ones are kept. */
    updateProfile(profile: ProfileWebModel): void {
      patchState(store, { profile: { ...profile, access: store.profile()!.access } });
    },
    clearProfile(): void {
      patchState(store, { profile: undefined });
    },
    setCountriesPhoneMasks(countriesPhoneMasks: PhoneFormatModel[]): void {
      patchState(store, { countriesPhoneMasks });
    },
  })),
  withMethods((store) => ({
    /** `tabId` identifies the browser tab, not the user, so it outlives the session. */
    _resetOnSessionEnd: rxMethod<void>(
      tap(() => patchState(store, { profile: undefined, countriesPhoneMasks: null })),
    ),
  })),
  withHooks({
    onInit(store) {
      store._resetOnSessionEnd(inject(SessionLifecycle).reset$);
    },
  }),
);
